import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { TimeSeriesChart, CameraCompareChart, PeakHoursChart, PieShareChart } from '../components/Charts';
import toast from 'react-hot-toast';

const PRESETS = [
  { label: 'Šiandien', days: 0,    groupBy: 'hour' },
  { label: '7 dienos', days: 7,    groupBy: 'day' },
  { label: '30 dienų', days: 30,   groupBy: 'day' },
  { label: '3 mėn.',   days: 90,   groupBy: 'day' },
  { label: '1 metai',  days: 365,  groupBy: 'month' },
  { label: '2 metai',  days: 730,  groupBy: 'month' },
  { label: '5 metai',  days: 1825, groupBy: 'month' },
];

const toLocal = d => { const dt = new Date(d); dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset()); return dt.toISOString().slice(0, 16); };

export default function Reports({ isAdmin }) {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [clients, setClients] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('all');
  const [from, setFrom] = useState(toLocal(Date.now() - 7*86400000));
  const [to,   setTo]   = useState(toLocal(Date.now()));
  const [groupBy, setGroupBy] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [preset, setPreset] = useState(1);

  useEffect(() => {
    if (isAdmin) api.get('/clients').then(r => setClients(r.data));
    api.get('/cameras').then(r => setCameras(r.data));
    loadData();
  }, []);

  function applyPreset(i) {
    const p = PRESETS[i]; setPreset(i); setGroupBy(p.groupBy);
    const now = new Date();
    const fr  = p.days === 0 ? new Date(now.setHours(0,0,0,0)) : new Date(Date.now() - p.days*86400000);
    setFrom(toLocal(fr)); setTo(toLocal(Date.now()));
  }

  async function loadData() {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from, to, groupBy });
      if (isAdmin && selectedClient) p.set('clientId', selectedClient);
      if (selectedCamera !== 'all') p.set('cameraId', selectedCamera);
      const res = await api.get(`/reports/data?${p}`);
      setData(res.data);
    } catch { toast.error('Klaida kraunant duomenis'); }

    // Užkrauti šventes
    try {
      const year  = new Date().getFullYear();
      const prevY = year - 1;
      const [r1, r2] = await Promise.all([
        api.get(`/holidays?year=${year}`),
        api.get(`/holidays?year=${prevY}`)
      ]);
      setHolidays([...(r1.data||[]), ...(r2.data||[])]);
    } catch(e) {}
    finally { setLoading(false); }
  }


  async function quickExport(period, format) {
    try {
      const token = localStorage.getItem('token');
      let url = '/api/reports/quick-export?period=' + period + '&format=' + format;
      if (isAdmin && selectedClient) url += '&clientId=' + selectedClient;
      const res = await fetch(url, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = 'srauto_' + period + '.' + format; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error('Eksporto klaida'); }
  }

  async function handleDownload(format) {
    const p = new URLSearchParams({ from, to });
    if (isAdmin && selectedClient) p.set('clientId', selectedClient);
    if (selectedCamera !== 'all') p.set('cameraId', selectedCamera);
    const res = await fetch(`/api/reports/export/${format}?${p}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (!res.ok) { toast.error('Eksporto klaida'); return; }
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `ataskaita.${format === 'excel' ? 'xlsx' : format}` });
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  async function handleSend() {
    if (!sendEmail) { toast.error('Įveskite el. paštą'); return; }
    setSending(true);
    try { await api.post('/reports/send-now', { email: sendEmail, from, to, clientId: selectedClient, cameraId: selectedCamera }); toast.success('Ataskaita išsiųsta!'); }
    catch (e) { toast.error(e.response?.data?.error || 'Klaida'); }
    finally { setSending(false); }
  }

  const clientCameras = isAdmin && selectedClient ? cameras.filter(c => String(c.client_id) === selectedClient) : cameras;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Ataskaitos ir grafikai</div>
        <div className="page-subtitle">Axis Object Analytics – žmonės, aut., dviračiai, autobusai, sunkvežimiai</div>
      </div>

      {/* Presets */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {PRESETS.map((p, i) => (
            <button key={i} className={`btn btn-sm ${preset===i ? 'btn-primary' : 'btn-secondary'}`} onClick={() => applyPreset(i)}>{p.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {isAdmin && (
            <div>
              <label className="form-label">Klientas</label>
              <select className="filter-input" value={selectedClient} onChange={e => { setSelectedClient(e.target.value); setSelectedCamera('all'); }}>
                <option value="">Visi</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="form-label">Kamera</label>
            <select className="filter-input" value={selectedCamera} onChange={e => setSelectedCamera(e.target.value)}>
              <option value="all">Visos</option>
              {clientCameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="form-label">Nuo</label><input className="filter-input" type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} /></div>
          <div><label className="form-label">Iki</label><input className="filter-input" type="datetime-local" value={to} onChange={e => setTo(e.target.value)} /></div>
          <div>
            <label className="form-label">Grupuoti</label>
            <select className="filter-input" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="hour">Valandomis</option>
              <option value="day">Dienomis</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={loadData} disabled={loading}>
            {loading ? <span className="loading-spinner" /> : '🔍 Rodyti'}
          </button>
        </div>
      </div>

      {/* Greitos ataskaitos */}
      <div className="card" style={{ marginBottom:16, padding:'16px 20px' }}>
        <div style={{ fontSize:14, fontWeight:700, marginBottom:12 }}>⚡ Greita suvestinė</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {[{period:'week',label:'Savaitė'},{period:'month',label:'Mėnuo'},{period:'year',label:'Metai'}].map(function(x) {
            return (
              <div key={x.period} style={{ display:'flex', gap:4 }}>
                <button className="btn btn-secondary" style={{ fontSize:12, padding:'6px 12px' }}
                  onClick={function(){ quickExport(x.period, 'xlsx'); }}>
                  📊 {x.label} Excel
                </button>
                <button className="btn btn-secondary" style={{ fontSize:12, padding:'6px 12px' }}
                  onClick={function(){ quickExport(x.period, 'pdf'); }}>
                  📄 {x.label} PDF
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Export */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Eksportuoti:</span>
        <button className="btn btn-success btn-sm" onClick={() => handleDownload('excel')}>📊 Excel (.xlsx)</button>
        <button className="btn btn-danger btn-sm"  onClick={() => handleDownload('pdf')}>📄 PDF</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <input className="filter-input" type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="Siųsti el. paštu..." style={{ width: 220 }} />
          <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending}>
            {sending ? <span className="loading-spinner" /> : '📧 Siųsti'}
          </button>
        </div>
      </div>

      {data && <>
        {/* Totals */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {[
            { label: 'Žmonės',       field: 'human', color: '#3b82f6', icon: '🚶' },
            { label: 'Automobiliai', field: 'car',   color: '#10b981', icon: '🚗' },
            { label: 'Dviračiai',    field: 'bike',  color: '#f59e0b', icon: '🚲' },
            { label: 'Autobusai',    field: 'bus',   color: '#8b5cf6', icon: '🚌' },
            { label: 'Sunkvežimiai', field: 'truck', color: '#ef4444', icon: '🚛' },
          ].map(s => (
            <div key={s.field} className="stat-card">
              <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{(data.totals[s.field] || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>

        <TimeSeriesChart data={data.timeSeries} title={`📈 Srautas – ${groupBy === 'hour' ? 'valandinis' : 'dienos'} grafikas`} type="area" holidays={holidays} />

        <div className="charts-grid">
          <CameraCompareChart data={data.perCamera} title="📷 Palyginimas pagal kamerą" />
          <PeakHoursChart data={data.timeSeries} title="⏰ Piko valandos" />
        </div>

        <div className="charts-grid">
          <PieShareChart data={data.perCamera} title="🥧 Srauto pasiskirstymas (kameromis)" />
          <div className="chart-container">
            <div className="chart-title">📋 Suvestinė pagal kamerą</div>
            <table>
              <thead><tr><th>Kamera</th><th>🚶</th><th>🚗</th><th>🚲</th><th>🚌</th><th>🚛</th></tr></thead>
              <tbody>
                {data.perCamera.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{c.camera}</td>
                    <td style={{ color: '#3b82f6' }}>{c.human.toLocaleString()}</td>
                    <td style={{ color: '#10b981' }}>{c.car.toLocaleString()}</td>
                    <td style={{ color: '#f59e0b' }}>{c.bike.toLocaleString()}</td>
                    <td style={{ color: '#8b5cf6' }}>{c.bus.toLocaleString()}</td>
                    <td style={{ color: '#ef4444' }}>{c.truck.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
